import { Checkbox, FormControlLabel } from '@mui/material'
import TextField from '@mui/material/TextField'
import { MRT_RowData } from 'material-react-table'
import { ReactNode, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { ColumnsPicker } from '@/components'
import { AceEditorField } from '@/components/AceEditor/AceEditorField'
import { AutoResizingTextFieldBase } from '@/components/AutoResizingTextField'
import { FormFieldsProps } from '@/components/FieldRenderer/types'
import { useTableContext } from '@/components/NewTable'
import { ForeignKey } from '@/components/Properties'
import { Select } from '@/components/UberForm'
import { useCanGenerateNaming } from '@/components/UberForm/Input/Naming/useCanGenerateNaming'
import { ItemPropertyType } from '@/enums'
import { resolveSelectOptions } from '@/helpers'
import { EnhancedAsyncSelect } from '@/lib/components/Select/EnhancedAsyncSelect/EnhancedAsyncSelect'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { useAdjustTableNameUsingPostMutation } from '@/rtkApi'
import { TableColumn } from '@/rtkApi/mddApiSliceGenerated'
import { TableColumnFlat } from '@/store/modules/table/types'
import { cn } from '@/utils/cn'

import { FormFieldLabel } from './components'
import { getUnavailableOptionClass } from './helpers'

export const formFieldSwitch: Record<
	ItemPropertyType,
	(props: FormFieldsProps<any>) => ReactNode
> = {
	[ItemPropertyType.ACE_EDITOR]: ({ fieldName, fieldProp }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		// Use useWatch with the control prop
		const stereotypeIdField = useWatch({
			control,
			name: 'stereotypeId',
		})

		const isDisabled = fieldProp?.meta?.isDisabled

		// Memoize disabled state calculation for better performance
		const disabled: boolean = useMemo(() => {
			if (!editMode) return true

			// Use custom disabled function if provided
			if (typeof isDisabled === 'function') return isDisabled(stereotypeIdField)

			return false
		}, [isDisabled, editMode, stereotypeIdField])

		return (
			<Controller
				control={control}
				name={fieldName}
				render={({ field }) => {
					return (
						<AceEditorField
							title={fieldProp.header}
							disabled={disabled}
							data-testid={fieldProp.id}
							{...field}
							value={field.value}
						/>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.CHECKBOX]: ({ fieldName, fieldProp }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		const row = useWatch({ control })

		// The placeholderRow mimics the data structure of the row that is available in MRT, so we can properly call the HOF (HIgh order functions.
		const placeholderRow = { original: row } as unknown as MRT_RowData & number

		const isFunctionalIsDisabled = Boolean(
			fieldProp.meta?.isDisabled &&
				typeof fieldProp.meta.isDisabled === 'function'
				? fieldProp.meta.isDisabled(placeholderRow)
				: fieldProp.meta?.isDisabled,
		)

		return (
			<Controller
				control={control}
				name={fieldName}
				render={({ field }) => {
					return (
						<FormControlLabel
							control={
								<Checkbox
									disabled={isFunctionalIsDisabled || !editMode}
									{...field}
									checked={field.value ?? false}
									data-testid={fieldProp.id}
								/>
							}
							label={fieldProp.header}
						/>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.OPTION]: ({ fieldProp, fieldName }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		const {
			optionsSource,
			optionLabel,
			optionValue,
			isNumeric,
			isDisabled,
			isLoading,
		} = fieldProp.meta || {}
		const { invalidColumnCodes, selectedRow } = useTableContext()

		const className = getUnavailableOptionClass(
			selectedRow?.columnCode,
			invalidColumnCodes,
		)

		// Use useWatch with the control prop
		const columnCodeField = useWatch({
			control,
			name: 'columnCode',
		})

		const options = resolveSelectOptions<TableColumnFlat>(
			optionsSource,
			columnCodeField,
		)

		// The placeholderRow mimics the data structure of the row that is available in MRT, so we can properly call the HOF (HIgh order functions.
		const placeholderRow = { original: selectedRow } as unknown as MRT_RowData &
			number

		const labelClassName = cn('pb-1', {
			'text-neutral-400': !editMode,
		})

		const isOptionDisabled: boolean =
			(typeof isDisabled === 'function'
				? isDisabled(placeholderRow)
				: isDisabled) || !editMode

		return (
			<Controller
				defaultValue={null}
				control={control}
				name={fieldName}
				render={({ field }) => {
					return (
						<>
							<label className={labelClassName} htmlFor="custom select">
								{fieldProp.header}
							</label>
							<Select
								className={className}
								options={options}
								valueKey={optionValue}
								labelKey={optionLabel}
								clearable
								isDisabled={isOptionDisabled}
								isNumeric={isNumeric}
								isLoading={isLoading}
								testId={fieldProp.id}
								{...field}
							/>
						</>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.TEXT]: ({ fieldName, fieldProp }) => {
		const {
			state: { editMode, node },
		} = useDetailTabContext()

		const { control, setValue } = useFormContext()
		const selectedRow = useWatch({ control })

		// Extract meta properties with proper defaults
		const { onBlur, isDisabled, isUpperCase } = fieldProp.meta || {}

		const { generateColumnCode } = useCanGenerateNaming(node)

		const [adjustTableNameMutation] = useAdjustTableNameUsingPostMutation()

		// Memoize disabled state calculation for better performance
		const disabled = useMemo(() => {
			// The placeholderRow mimics the data structure of the row that is available in MRT, so we can properly call the HOF (HIgh order functions.
			const placeholderRow = {
				original: selectedRow,
			} as unknown as MRT_RowData & number

			if (!editMode) return true

			// Auto-generated code fields should be disabled
			if (generateColumnCode && fieldName === 'code') return true

			// Use custom disabled function if provided
			if (typeof isDisabled === 'function') return isDisabled?.(placeholderRow)
		}, [generateColumnCode, fieldName, isDisabled, editMode, selectedRow])

		return (
			<Controller
				control={control}
				name={fieldName}
				render={({ field }) => {
					return (
						<TextField
							{...field}
							onBlur={() =>
								onBlur?.({
									adjustTableNameMutation,
									fieldValue: field.value,
									isUpperCase: isUpperCase as boolean,
									setFormValue: setValue,
								})
							}
							disabled={disabled as boolean}
							fullWidth
							label={fieldProp.header}
							size="small"
							// This fallback must stay, otherwise it breaks UI (overlaps)
							value={field.value ?? ''}
							data-testid={fieldProp.id}
						/>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.TEXTAREA]: ({ fieldName, fieldProp }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		return (
			<Controller
				control={control}
				name={fieldName}
				render={({ field }) => (
					<AutoResizingTextFieldBase
						{...field}
						className="w-full"
						disabled={!editMode}
						label={fieldProp.header}
						value={field.value ?? ''}
						data-testid={fieldProp.id}
					/>
				)}
			/>
		)
	},
	[ItemPropertyType.FOREIGN_KEY]: ({ fieldProp }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const {
			possibleForeignKeys,
			foreignKeyTableId,
			primaryKeys,
			optionValue,
			optionLabel,
			columns,
		} = fieldProp.meta || {}

		return (
			<>
				<FormFieldLabel editMode={editMode} labelName={fieldProp.header} />
				<ForeignKey
					columns={columns}
					disabled={!editMode}
					foreignKeyTableId={foreignKeyTableId}
					optionLabel={optionLabel || ''}
					optionValue={optionValue || ''}
					possibleForeignKeys={possibleForeignKeys || {}}
					primaryKeys={primaryKeys || []}
				/>
			</>
		)
	},
	[ItemPropertyType.COLUMNS]: ({ fieldName, fieldProp }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		return (
			<Controller
				control={control}
				name={fieldName}
				render={({ field }) => {
					const { optionsSource } = fieldProp.meta || {}

					const columns = optionsSource as TableColumn[]

					return (
						<>
							<FormFieldLabel
								editMode={editMode}
								labelName={fieldProp.header}
							/>

							<ColumnsPicker
								field={fieldName}
								columns={columns}
								{...field}
								editMode={editMode}
								testId={fieldProp.id}
							/>
						</>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.COLUMNS_WITH_ORDER]: ({ fieldName, fieldProp }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		return (
			<Controller
				control={control}
				name={fieldName}
				render={({ field }) => {
					const { optionsSource } = fieldProp.meta || {}

					const columns = optionsSource as TableColumn[]

					return (
						<>
							<FormFieldLabel
								editMode={editMode}
								labelName={fieldProp.header}
							/>

							<ColumnsPicker
								field={fieldName}
								columns={columns}
								{...field}
								editMode={editMode}
								orders
								testId={fieldProp.id}
							/>
						</>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.ASYNC_SELECT]: ({ fieldProp, fieldName }) => {
		const {
			state: { editMode },
		} = useDetailTabContext()

		const { control } = useFormContext()

		const {
			optionsSource,
			optionLabel,
			optionValue,
			isNumeric,
			isLoading,
			loadOptions,
			formatOptionLabel,
			components,
		} = fieldProp.meta || {}

		const options = resolveSelectOptions<TableColumnFlat>(optionsSource)

		const labelClassName = cn('pb-1', {
			'text-neutral-400': !editMode,
		})

		if (!optionLabel || !optionValue) return null

		return (
			<Controller
				defaultValue={null}
				control={control}
				name={fieldName}
				render={({ field }) => {
					return (
						<>
							<label className={labelClassName} htmlFor="custom select">
								{fieldProp.header}
							</label>
							<EnhancedAsyncSelect
								isDisabled={!editMode}
								isMulti={false}
								isNumeric={isNumeric}
								isLoading={isLoading}
								labelKey={optionLabel}
								valueKey={optionValue}
								testId={fieldProp.id}
								options={options}
								loadOptions={loadOptions}
								formatOptionLabel={formatOptionLabel}
								components={components}
								useBaseStyles
								{...field}
								value={field.value || ''}
							/>
						</>
					)
				}}
			/>
		)
	},
	[ItemPropertyType.HIDDEN]: () => <></>,
}
